import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import { Box, Card, DialogActions, DialogContent, Toolbar, useTheme } from "@mui/material";
import { IStore } from "../../../../hooks/stores";
import { strings } from "../../../../localization/LocalizedStrings";
import { TextField } from "../../../TextField";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { useAddStore } from "../../../../hooks/stores/useAddStore";
import { useModifyStore } from "../../../../hooks/stores/useModifyStore";
import { useDeleteStore } from "../../../../hooks/stores/useDeleteStore";
import { AlertDialog } from "../../../AlertDialog";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { FieldTitle } from "../../../Text/FieldTitle";
import { ContainedButton } from "../../../ContainedButton";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { FreeTextListItem } from "../../../FreeTextListItem";
import { ModifyFreeTextAttributeDialog } from "../../ModifyFreeTextAttributeDialog";
import { Firebase } from "../../../../services/Firebase";
import { ProductPurchasesDialog } from "../../../products/ProductPurchasesDialog";
import { ProductType } from "../../../../hooks/products/interfaces";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";

export const ModifyStoreDialog: FunctionComponent<IModifyStoreDialogProps> = ({
  open,
  onClose,
  store,
  onNewItem,
  onModifiedItem,
  onDeletedItem
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const dialogTitle = store ? strings.store_title : strings.ms_new_title;
  const { addStore } = useAddStore();
  const { modifyStore } = useModifyStore();
  const { deleteStore } = useDeleteStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isModifyProfileDialogOpen, setIsModifyProfileDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [isPurchaseAlertOpen, setIsPurchaseAlertOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.ms_missing_error)
  });
  const formik = useFormik({
    initialValues: {
      name: store?.name ?? "",
      address: store?.address ?? "",
      profile: store?.profile ?? "",
      website: store?.website ?? ""
    },
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async ({ name, address, website, profile }) => {
      setIsLoading(true);
      if (store) {
        const { status, body } = await modifyStore({
          uuid: store.uuid,
          name: name,
          address: address,
          website: website,
          profile: profile
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_store_action", { isNew: false });
          onModifiedItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      } else {
        const { status, body } = await addStore({
          name: name,
          address: address,
          website: website,
          profile: profile
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_store_action", { isNew: true });
          onNewItem?.(body);
          onClose();
        } else if (StatusCodesHelper.isLimitReached(status)) {
          setAlertMessage(body.message);
          setIsPurchaseAlertOpen(true);
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  const onDelete = async () => {
    if (!store) return;
    setIsLoading(true);
    const { status, body } = await deleteStore({ uuid: store.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      onDeletedItem?.(store);
      onClose();
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {store && (
            <ToolbarButton
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.ms_field_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="name"
              name="name"
              size="small"
              autoFocus={!isMobile}
              placeholder={strings.ms_placeholder}
              value={formik.values.name}
              onChange={formik.handleChange}
              enterAction={formik.submitForm}
            />
          </div>
          <FieldTitle>{strings.ma_website_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="website"
              name="website"
              size="small"
              placeholder={strings.ma_website_placeholder}
              value={formik.values.website}
              onChange={formik.handleChange}
              enterAction={formik.submitForm}
            />
          </div>
          <FieldTitle>{strings.ma_profile_title}</FieldTitle>
          <Card className={styles.profileCard}>
            <FreeTextListItem
              hideTitle
              detail={formik.values.profile ?? "--"}
              maxLines={4}
              onClick={() => setIsModifyProfileDialogOpen(true)}
            />
          </Card>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <ModifyFreeTextAttributeDialog
        title={strings.ma_profile_placeholder}
        description={strings.edit_profile_description}
        placeholder={strings.ma_profile_placeholder}
        value={formik.values.profile}
        open={isModifyProfileDialogOpen}
        onClose={() => setIsModifyProfileDialogOpen(false)}
        onChange={newValue => formik.setFieldValue("profile", newValue)}
        onClearValue={() => formik.setFieldValue("profile", null)}
      />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
      <AlertDialog
        message={alertMessage}
        open={isPurchaseAlertOpen}
        setOpen={setIsPurchaseAlertOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setIsPurchaseDialogOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.STORES}
        open={isPurchaseDialogOpen}
        setOpen={setIsPurchaseDialogOpen}
      />
    </FormikProvider>
  );
};

export interface IModifyStoreDialogProps {
  open: boolean;
  onClose: () => void;
  store?: IStore;
  onNewItem?: (item: IStore) => void;
  onModifiedItem?: (item: IStore) => void;
  onDeletedItem?: (item: IStore) => void;
}
